import {
  Dialog,
  DialogActions as MaterialDialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import {Button} from '@wandb/weave/components/Button';
import {Tag} from '@wandb/weave/components/Tag';
import React, {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';

import {CopyableId} from '../../common/Id';
import {TraceObjSchemaForBaseObjectClass} from '../../wfReactInterface/objectClassQuery';
import {LLMMaxTokensKey} from '../llmMaxTokens';
import {
  OptionalTraceCallSchema,
  PlaygroundState,
  SavedPlaygroundModelState,
} from '../types';
import {DEFAULT_SAVED_MODEL} from '../usePlaygroundState';
import {LLMDropdown, ProviderOption} from './LLMDropdown';
import {setLastUsedModel} from './LLMDropdown/modelCache';
import {PromptDropdown, PromptOption} from './PromptDropdown';
import {
  SetPlaygroundStateFieldFunctionType,
  TraceCallOutput,
} from './useChatFunctions';

type PlaygroundChatTopBarProps = {
  idx: number;
  settingsTab: number | null;
  setSettingsTab: (tab: number | null) => void;
  setPlaygroundStateField: SetPlaygroundStateFieldFunctionType;
  entity: string;
  project: string;
  playgroundStates: PlaygroundState[];
  setPlaygroundStates: (playgroundStates: PlaygroundState[]) => void;
  isTeamAdmin: boolean;
  refetchConfiguredProviders: () => void;
  refetchCustomLLMs: () => void;
  llmDropdownOptions: ProviderOption[];
  areProvidersLoading: boolean;
  customProvidersResult: TraceObjSchemaForBaseObjectClass<'Provider'>[];
  promptOptions: PromptOption[];
  promptsLoading: boolean;
};

const DialogActions = styled(MaterialDialogActions)<{$align: string}>`
  justify-content: ${({$align}) =>
    $align === 'left' ? 'flex-start' : 'flex-end'} !important;
  padding: 32px 32px 32px 32px !important;
`;
DialogActions.displayName = 'S.DialogActions';

export const PlaygroundChatTopBar: React.FC<PlaygroundChatTopBarProps> = ({
  idx,
  settingsTab,
  setSettingsTab,
  setPlaygroundStateField,
  entity,
  project,
  playgroundStates,
  setPlaygroundStates,
  isTeamAdmin,
  refetchConfiguredProviders,
  refetchCustomLLMs,
  llmDropdownOptions,
  areProvidersLoading,
  customProvidersResult,
  promptOptions,
  promptsLoading,
}) => {
  const history = useHistory();
  const onlyOneChat = playgroundStates.length === 1;
  const [confirmClear, setConfirmClear] = useState(false);

  const clearCall = (index: number) => {
    history.push(`/${entity}/${project}/weave/playground`);
    setPlaygroundStateField(index, 'traceCall', {
      project_id: `${entity}/${project}`,
      id: '',
      inputs: {
        messages: [],
      },
    } as OptionalTraceCallSchema);
  };

  const handleModelChange = (
    index: number,
    model: LLMMaxTokensKey,
    maxTokens: number,
    maxTokensDefault: number,
    savedModel?: SavedPlaygroundModelState
  ) => {
    // Persist the model selection to localStorage
    setLastUsedModel(entity, project, model);

    if (!savedModel) {
      setPlaygroundStates(
        playgroundStates.map((state, i) => {
          if (i === index) {
            return {
              ...state,
              model,
              maxTokensLimit: maxTokens,
              maxTokens: maxTokensDefault,
            };
          }
          return state;
        })
      );
      return;
    }

    const {messagesTemplate, ...defaultParams} =
      savedModel?.savedModelParams ?? {};

    // Filter out undefined values from defaultParams
    const filteredDefaultParams = Object.fromEntries(
      Object.entries(defaultParams).filter(([_, value]) => value !== undefined)
    );

    setPlaygroundStates(
      playgroundStates.map((state, i) => {
        if (i === index) {
          const newState = {
            ...state,
            model,
            maxTokensLimit: maxTokens,
            maxTokens: maxTokensDefault,

            // Update the state to show we are using a saved model
            savedModel: savedModel ?? DEFAULT_SAVED_MODEL,
            traceCall: {
              ...state.traceCall,
              inputs: {
                ...state.traceCall?.inputs,
                // If the saved model has messages, use them, otherwise use the current messages
                messages: messagesTemplate ?? state.traceCall?.inputs?.messages,
              },
              output: {
                ...(state.traceCall?.output as TraceCallOutput),
                // If the saved model has messages, clear the choices, otherwise use the current choices
                choices: messagesTemplate
                  ? undefined
                  : (state.traceCall?.output as TraceCallOutput)?.choices,
              },
            },
            // Update the other params
            ...filteredDefaultParams,
          };

          return newState;
        }
        return state;
      })
    );
  };

  const ConfirmClearModal: React.FC<{
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }> = ({open, onClose, onConfirm}) => {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Clear chat history</DialogTitle>
        <DialogContent style={{overflow: 'hidden'}}>
          <p>Are you sure you want to clear the chat history?</p>
        </DialogContent>
        <DialogActions $align="left">
          <Button variant="destructive" onClick={onConfirm}>
            Clear history
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handlePromptChange = useCallback(
    (promptName: string, messages: any[], promptRef: string) => {
      // Update the prompt state
      setPlaygroundStateField(idx, 'prompt', {
        promptName,
        promptRef,
        messages: JSON.parse(JSON.stringify(messages)), // Deep copy for template preservation
        isModified: false, // Reset modified flag when loading a new prompt
      });
    },
    [idx, setPlaygroundStateField]
  );

  const state = playgroundStates[idx];

  // Auto-resolve promptRef when loading a call with a prompt but no promptName set
  useEffect(() => {
    const promptRef = state.prompt?.promptRef;
    const promptName = state.prompt?.promptName;

    // If we have a promptRef but no promptName (or an incorrect one), resolve it from promptOptions
    if (promptRef && !promptsLoading && promptOptions.length > 0) {
      // Parse the weave URI to extract object ID and digest
      const match = promptRef.match(/object\/([^:]+):(.+)/);
      if (match) {
        const promptObjectId = match[1];
        const digest = match[2];

        // Find the prompt in promptOptions
        const promptOption = promptOptions.find(
          opt => opt.value === promptObjectId
        );

        if (promptOption) {
          // Try to find the specific version by digest
          const specificVersion = promptOption.versions.find(
            v => v.digest === digest
          );
          const versionToUse = specificVersion || promptOption.latestVersion;

          if (versionToUse) {
            // Only update if the promptName doesn't match what we expect
            if (promptName !== versionToUse.value) {
              setPlaygroundStateField(idx, 'prompt', {
                promptName: versionToUse.value, // Format: "name:vX"
                messages: versionToUse.messages,
                promptRef, // Keep the full weave URI
                isModified: false,
              });
            }
          }
        }
      }
    }
  }, [
    state.prompt?.promptRef,
    state.prompt?.promptName,
    promptOptions,
    promptsLoading,
    idx,
    setPlaygroundStateField,
  ]);

  return (
    <div className="flex w-full flex-col gap-3">
      {/* First row: Number, model icon, model name, save button, close button */}
      <div className="flex w-full justify-between items-center">
        <div className="flex items-center gap-3">
          <Tag label={`${idx + 1}`} />
          <Button icon="model" variant="ghost" size="medium" disabled />
          <span className="text-medium text-moon-600">
            {playgroundStates[idx].savedModel?.objectId || 'Untitled model'}
          </span>
          {playgroundStates[idx].traceCall?.id && (
            <div className="flex items-center">
              <CopyableId
                id={playgroundStates[idx]!.traceCall!.id!}
                type="Call"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            tooltip={'Clear chat'}
            icon="randomize-reset-reload"
            size="medium"
            variant="ghost"
            onClick={() => setConfirmClear(true)}
          />
          <Button
            endIcon="close"
            size="medium"
            variant="ghost"
            disabled={onlyOneChat}
            tooltip={onlyOneChat ? 'Cannot remove last chat' : 'Remove chat'}
            onClick={() => {
              // If the settings tab is set to length that is going to be removed,
              // we need to set the settings tab to the previous chat.
              if (settingsTab && settingsTab < playgroundStates.length + 1) {
                setSettingsTab(settingsTab - 1);
              }
              setPlaygroundStates(
                playgroundStates.filter((_, index) => index !== idx)
              );
            }}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-moon-200 mb-8"></div>

      {/* Second row: LLM dropdown, settings button, settings summary */}
      <div className="flex w-full items-center gap-4 mb-8">
        <LLMDropdown
          value={playgroundStates[idx].model}
          onChange={(model, maxTokens, maxTokensDefault, savedModel) =>
            handleModelChange(
              idx,
              model as LLMMaxTokensKey,
              maxTokens,
              maxTokensDefault,
              savedModel
            )
          }
          entity={entity}
          project={project}
          isTeamAdmin={isTeamAdmin}
          refetchConfiguredProviders={refetchConfiguredProviders}
          refetchCustomLLMs={refetchCustomLLMs}
          llmDropdownOptions={llmDropdownOptions}
          areProvidersLoading={areProvidersLoading}
          customProvidersResult={customProvidersResult}
          direction={{horizontal: 'right'}}
          selectFirstAvailable={true}
          className="w-[300px]"
        />
        <Button
          tooltip={'Chat settings'}
          icon="settings-parameters"
          size="medium"
          variant="ghost"
          active={settingsTab === idx}
          onClick={() => {
            if (settingsTab === idx) {
              setSettingsTab(null);
            } else {
              setSettingsTab(idx);
            }
          }}
        />
        <div className="flex items-center gap-2 text-xs text-moon-500 overflow-hidden">
          <span className="whitespace-nowrap">
            Num trials: {playgroundStates[idx].nTimes},
          </span>
          <span className="whitespace-nowrap">
            Temperature: {playgroundStates[idx].temperature},
          </span>
          <span className="whitespace-nowrap truncate">
            Response format: {playgroundStates[idx].responseFormat}
          </span>
        </div>
      </div>

      {/* Third row: Prompt dropdown */}
      <div className="flex items-center gap-2">
        <PromptDropdown
          entity={entity}
          project={project}
          value={playgroundStates[idx].prompt?.promptName || ''}
          onChange={handlePromptChange}
          className="w-[200px]"
          promptOptions={promptOptions}
          loading={promptsLoading}
        />
      </div>
      <ConfirmClearModal
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => {
          clearCall(idx);
          setConfirmClear(false);
        }}
      />
    </div>
  );
};
